import type { ScanBarState } from '../shell/scanFocus'
import { StationShell } from '../ui/StationShell'
import type { CameraState } from '../ui/ScanBar'
import { MOVEMENT_REASONS, type MovementReason } from '../data/writes'

/**
 * The five-second bar (ops-bottlenecks s10, the hard bar quoted): scan the book,
 * tap a reason, done. Reasons as big buttons - Family, Gift, Returned, Damaged. No
 * typing, no confirmation screen, no navigating to a station first.
 *
 * The reason is chosen BEFORE the scan and stays chosen, because a book going
 * upstairs to Tara is usually one of several in a row, and re-tapping the reason
 * per book is the friction that makes people stop recording at all. `POST
 * /movement/scan` is one round trip, so the scan itself is the commit.
 *
 * Why this screen at all: the alternative is the ledger rotting quietly, and a
 * rotten audit trail is worse than none because it is trusted.
 */
export interface MovementProps {
  scan: ScanBarState
  camera?: CameraState
  videoRef?: React.Ref<HTMLVideoElement>
  reason: MovementReason | null
  onReason: (reason: MovementReason) => void
  /** Recorded this session, newest first - the proof the taps did something. */
  recorded: Array<{ movementId: string; unitId: string; reason: string; state: string }>
  online?: boolean
}

const REASON_LABEL: Record<MovementReason, string> = {
  family: 'Family',
  gift: 'Gift',
  returned: 'Returned',
  damaged: 'Damaged',
}

export function Movement({ scan, camera, videoRef, reason, onReason, recorded, online = true }: MovementProps) {
  return (
    <StationShell
      title="Take a book"
      progress={`${recorded.length} recorded this session`}
      scan={scan}
      camera={camera}
      videoRef={videoRef}
      online={online}
    >
      <p className="t-label">Why is it leaving the shelf?</p>
      <div className="reason-grid">
        {MOVEMENT_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            className={`btn reason-btn${r === reason ? ' reason-on' : ''}`}
            aria-pressed={r === reason}
            onClick={() => onReason(r)}
          >
            {REASON_LABEL[r]}
          </button>
        ))}
      </div>

      {reason ? (
        <p className="t-label muted move-ready">
          Now scan the book. Every scan is recorded as <strong>{REASON_LABEL[reason]}</strong> until you change it.
        </p>
      ) : (
        <p className="t-label muted">Tap a reason first. Nothing is recorded without one.</p>
      )}

      {recorded.length ? (
        <ul className="row-list movement-list">
          {recorded.map((m) => (
            <li key={m.movementId} className="row" data-movement-id={m.movementId} data-unit-id={m.unitId}>
              <span className="row-title">{m.unitId}</span>
              <span className="chip">{m.reason}</span>
              <span className="muted">{m.state.replace(/_/g, ' ')}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </StationShell>
  )
}
